import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil } from 'lucide-react';
import { EditField } from '../../../../components/EditPanel/types';
import { EditPanel } from '../../../../components/EditPanel/EditPanel';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  FloatingButtonWidgetProps,
  FloatingButtonWidgetState,
  FloatingButtonShape,
  FloatingButtonStyle,
} from './types';
import { useWidgetEditable } from '../../../../hooks/useWidgetEditable';
import { useEditMode } from '../../../../utils/useEditMode';
import { dispatchPanelOpened, subscribeToPanelOpened } from '../../../../utils/editPanelEvents';
import colors from '../../../../colors/colors';
import { themeColor, ColorValue } from '../../../../types/color';

// ============================================================================
// Style preset definitions
// ============================================================================

interface StylePresetValues {
  backgroundColor: ColorValue;
  backgroundColorHover: ColorValue;
  iconColor: ColorValue;
  borderWidth: number;
  borderColor: ColorValue;
  shadowOpacity: number;
  shadowBlur: number;
  shadowSpread: number;
  shadowOffsetY: number;
}

const stylePresets: Record<FloatingButtonStyle, StylePresetValues> = {
  filled: {
    backgroundColor: themeColor('primary'),
    backgroundColorHover: themeColor('primary'),
    iconColor: themeColor('secondary'),
    borderWidth: 0,
    borderColor: themeColor('transparent'),
    shadowOpacity: 0.25,
    shadowBlur: 50,
    shadowSpread: -12,
    shadowOffsetY: 25,
  },
  light: {
    backgroundColor: themeColor('background'),
    backgroundColorHover: themeColor('background'),
    iconColor: themeColor('primary'),
    borderWidth: 0,
    borderColor: themeColor('transparent'),
    shadowOpacity: 0.12,
    shadowBlur: 24,
    shadowSpread: -4,
    shadowOffsetY: 8,
  },
  outlined: {
    backgroundColor: themeColor('background'),
    backgroundColorHover: themeColor('background'),
    iconColor: themeColor('primary'),
    borderWidth: 1.5,
    borderColor: themeColor('textPrimary'),
    shadowOpacity: 0,
    shadowBlur: 0,
    shadowSpread: 0,
    shadowOffsetY: 0,
  },
};

const shapePresets: Record<FloatingButtonShape, { borderRadius: number }> = {
  'rounded-square': { borderRadius: 25 },
  circle: { borderRadius: 50 },
};

const defaultState: FloatingButtonWidgetState = {
  // Preset selectors
  buttonShape: 'circle',
  buttonStyle: 'filled',

  // Position
  position: 'bottom-right',
  horizontalOffset: 20,
  verticalOffset: 20,

  // Size
  size: 44,

  // Colors (using theme colors) — derived from "filled" style
  backgroundColor: themeColor('primary'),
  backgroundColorHover: themeColor('primary'),
  iconColor: themeColor('secondary'),

  // Border — derived from "filled" style
  borderWidth: 0,
  borderColor: themeColor('transparent'),

  // Shape — derived from "circle" shape
  borderRadius: 50, // percentage - 50 = circle

  // Shadow — derived from "filled" style
  shadowColor: themeColor('primary'),
  shadowOpacity: 0.25,
  shadowBlur: 50,
  shadowSpread: -12,
  shadowOffsetY: 25,

  // Icon
  iconStyle: 'chat-ai',
  iconSize: 22,

  // Animation
  hoverScale: 1.1,
  animationDuration: 200,

  // Suggested Messages
  showSuggestedMessages: true,
  suggestedMessages: undefined,
  suggestedMessageRotationMs: 4000,

  // Suggested Pills Styling
  pillBackgroundColor: '#ffffff',
  pillTextColor: '#1f2937',
  pillBorderRadius: 12,
  pillFontSize: 14,
  pillPaddingX: 16,
  pillPaddingY: 10,
  pillShadowOpacity: 0.12,
  pillsToShow: 2,

  // Header Styling
  pillHeaderText: undefined,
  pillHeaderColor: '#6b7280',
  pillHeaderFontSize: 11,

  // Feature Prompt Pill
  featurePromptBgColor: themeColor('primary'),
  featurePromptTextColor: themeColor('secondary'),
  featurePromptBorderWidth: 0,
  featurePromptBorderColor: themeColor('transparent'),
  featurePromptBorderRadius: 12,
  featurePromptFontSize: 14,
  featurePromptFontWeight: 500,

  // Typography
  fontFamily: 'inherit',
};

const editFields: EditField[] = [
  // --- Preset selectors ---
  {
    key: 'iconStyle',
    label: 'Icon',
    type: 'options',
    options: [
      { label: 'Chat AI', value: 'chat-ai' },
      { label: 'Search Sparkle', value: 'search-sparkle' },
      { label: 'Three Sparkles', value: 'three-sparkles' },
      { label: 'Diamond Sparkles', value: 'diamond-sparkles' },
      { label: 'Search Star', value: 'search-star' },
      { label: 'Search Sparkle Alt', value: 'search-sparkle-alt' },
    ],
  },
  {
    key: 'buttonShape',
    label: 'Shape',
    type: 'options',
    options: [
      { label: 'Circle', value: 'circle' },
      { label: 'Rounded Square', value: 'rounded-square' },
    ],
  },
  {
    key: 'buttonStyle',
    label: 'Style',
    type: 'options',
    options: [
      { label: 'Filled', value: 'filled' },
      { label: 'Light', value: 'light' },
      { label: 'Outlined', value: 'outlined' },
    ],
  },

  // --- Size & Position ---
  {
    key: 'size',
    label: 'Button Size',
    type: 'number',
    min: 32,
    max: 72,
    step: 4,
    group: 'Size & Position',
  },
  {
    key: 'iconSize',
    label: 'Icon Size',
    type: 'number',
    min: 16,
    max: 40,
    step: 2,
    group: 'Size & Position',
  },
  {
    key: 'position',
    label: 'Position',
    type: 'options',
    options: [
      { label: 'Bottom Right', value: 'bottom-right' },
      { label: 'Bottom Left', value: 'bottom-left' },
      { label: 'Top Right', value: 'top-right' },
      { label: 'Top Left', value: 'top-left' },
    ],
    group: 'Size & Position',
  },
  {
    key: 'horizontalOffset',
    label: 'Horizontal Offset (px)',
    type: 'number',
    min: 8,
    max: 100,
    step: 4,
    group: 'Size & Position',
  },
  {
    key: 'verticalOffset',
    label: 'Vertical Offset (px)',
    type: 'number',
    min: 8,
    max: 100,
    step: 4,
    group: 'Size & Position',
  },

  // --- Colors ---
  { key: 'backgroundColor', label: 'Background Color', type: 'color', group: 'Colors' },
  { key: 'backgroundColorHover', label: 'Hover Background', type: 'color', group: 'Colors' },
  { key: 'iconColor', label: 'Icon Color', type: 'color', group: 'Colors' },

  // --- Shape ---
  {
    key: 'borderRadius',
    label: 'Border Radius (%)',
    type: 'number',
    min: 0,
    max: 50,
    step: 5,
    group: 'Shape',
  },
  {
    key: 'borderWidth',
    label: 'Border Width',
    type: 'number',
    min: 0,
    max: 4,
    step: 0.5,
    group: 'Shape',
  },
  { key: 'borderColor', label: 'Border Color', type: 'color', group: 'Shape' },

  // --- Shadow ---
  { key: 'shadowColor', label: 'Shadow Color', type: 'color', group: 'Shadow' },
  {
    key: 'shadowOpacity',
    label: 'Shadow Opacity',
    type: 'number',
    min: 0,
    max: 1,
    step: 0.05,
    group: 'Shadow',
  },
  {
    key: 'shadowBlur',
    label: 'Shadow Blur',
    type: 'number',
    min: 0,
    max: 100,
    step: 5,
    group: 'Shadow',
  },
  {
    key: 'shadowSpread',
    label: 'Shadow Spread',
    type: 'number',
    min: -20,
    max: 20,
    step: 2,
    group: 'Shadow',
  },
  {
    key: 'shadowOffsetY',
    label: 'Shadow Offset Y',
    type: 'number',
    min: 0,
    max: 50,
    step: 5,
    group: 'Shadow',
  },

  // Animation
  {
    key: 'hoverScale',
    label: 'Hover Scale',
    type: 'number',
    min: 1,
    max: 1.3,
    step: 0.05,
  },
  {
    key: 'animationDuration',
    label: 'Animation Duration (ms)',
    type: 'number',
    min: 100,
    max: 500,
    step: 50,
  },

  // Suggested Messages
  {
    key: 'showSuggestedMessages',
    label: 'Show Suggested Messages',
    type: 'toggle',
  },
  {
    key: 'suggestedMessageRotationMs',
    label: 'Message Rotation (ms)',
    type: 'number',
    min: 2000,
    max: 10000,
    step: 500,
  },
  {
    key: 'pillsToShow',
    label: 'Number of Pills',
    type: 'number',
    min: 1,
    max: 4,
    step: 1,
  },

  // Pill Styling
  { key: 'pillBackgroundColor', label: 'Pill Background', type: 'color' },
  { key: 'pillTextColor', label: 'Pill Text Color', type: 'color' },
  {
    key: 'pillBorderRadius',
    label: 'Pill Border Radius',
    type: 'number',
    min: 0,
    max: 24,
    step: 2,
  },
  {
    key: 'pillFontSize',
    label: 'Pill Font Size',
    type: 'number',
    min: 10,
    max: 18,
    step: 1,
  },
  {
    key: 'pillPaddingX',
    label: 'Pill Padding X',
    type: 'number',
    min: 8,
    max: 24,
    step: 2,
  },
  {
    key: 'pillPaddingY',
    label: 'Pill Padding Y',
    type: 'number',
    min: 6,
    max: 20,
    step: 2,
  },
  {
    key: 'pillShadowOpacity',
    label: 'Pill Shadow Opacity',
    type: 'number',
    min: 0,
    max: 0.5,
    step: 0.02,
  },

  // Header Styling
  {
    key: 'pillHeaderText',
    label: 'Header Text (Override)',
    type: 'text',
  },
  { key: 'pillHeaderColor', label: 'Header Text Color', type: 'color' },
  {
    key: 'pillHeaderFontSize',
    label: 'Header Font Size',
    type: 'number',
    min: 9,
    max: 14,
    step: 1,
  },

  // Feature Prompt Pill
  {
    key: 'featurePromptBgColor',
    label: 'Prompt Pill Background',
    type: 'color',
    group: 'Feature Prompt',
  },
  {
    key: 'featurePromptTextColor',
    label: 'Prompt Pill Text Color',
    type: 'color',
    group: 'Feature Prompt',
  },
  {
    key: 'featurePromptBorderWidth',
    label: 'Prompt Pill Border Width',
    type: 'number',
    min: 0,
    max: 4,
    step: 0.5,
    group: 'Feature Prompt',
  },
  {
    key: 'featurePromptBorderColor',
    label: 'Prompt Pill Border Color',
    type: 'color',
    group: 'Feature Prompt',
  },
  {
    key: 'featurePromptBorderRadius',
    label: 'Prompt Pill Border Radius',
    type: 'number',
    min: 0,
    max: 24,
    step: 2,
    group: 'Feature Prompt',
  },
  {
    key: 'featurePromptFontSize',
    label: 'Prompt Pill Font Size',
    type: 'number',
    min: 10,
    max: 20,
    step: 1,
    group: 'Feature Prompt',
  },
  {
    key: 'featurePromptFontWeight',
    label: 'Prompt Pill Font Weight',
    type: 'number',
    min: 300,
    max: 700,
    step: 100,
    group: 'Feature Prompt',
  },
  {
    key: 'fontFamily',
    label: 'Prompt Pill Font Family',
    type: 'font',
    group: 'Feature Prompt',
  },
];

// Helper to get edit icon position based on button position
const getEditIconPosition = (state: FloatingButtonWidgetState): React.CSSProperties => {
  const { position, horizontalOffset, verticalOffset, size } = state;
  const styles: React.CSSProperties = {
    position: 'fixed',
    zIndex: 2147483647, // Max z-index to ensure it's on top
    minWidth: 0,
    minHeight: 0,
  };

  // Position edit icon at top-right of the button
  if (position.startsWith('bottom')) {
    styles.bottom = verticalOffset + size - 8; // 8px overlap
  } else {
    styles.top = verticalOffset - 8;
  }

  if (position.endsWith('left')) {
    styles.left = horizontalOffset + size - 8;
  } else {
    styles.right = horizontalOffset - 8;
  }

  return styles;
};

export const FloatingButtonWidget: React.FC<FloatingButtonWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [screenPosition, setScreenPosition] = useState({ x: 0, y: 0 });
  const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null);
  const [showExternalCloseDialog, setShowExternalCloseDialog] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const settingButtonRef = useRef<HTMLButtonElement>(null);
  const isEditMode = useEditMode();

  const {
    state,
    isLoading,
    isSaving,
    handleEditChange: rawHandleEditChange,
    handleSave: saveWidget,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
    studioLockedKeys,
  } = useWidgetEditable<FloatingButtonWidgetState>(widgetId, { ...defaultState, ...initialState }, editFields);

  // Resolve the theme font key (e.g. "studio-brand-font") — the pill inherits
  // the host page's font when unset or unresolvable.
  const fontKey = state.fontFamily;
  const fontData = fontKey && fontKey !== 'inherit' ? themeFonts[fontKey] : null;
  const googleFontLink = fontData && fontData.format === 'google' ? fontData.url : null;
  useEffect(() => {
    if (!googleFontLink) return;
    if (document.querySelector(`link[href="${googleFontLink}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = googleFontLink;
    document.head.appendChild(link);
  }, [googleFontLink]);

  // Wrap handleEditChange to auto-apply presets when shape/style changes
  const handleEditChange = useCallback(
    (key: string, value: any) => {
      rawHandleEditChange(key, value);

      if (key === 'buttonStyle') {
        const preset = stylePresets[value as FloatingButtonStyle];
        if (preset) {
          Object.entries(preset).forEach(([k, v]) => {
            rawHandleEditChange(k, v);
          });
        }
      }

      if (key === 'buttonShape') {
        const preset = shapePresets[value as FloatingButtonShape];
        if (preset) {
          rawHandleEditChange('borderRadius', preset.borderRadius);
        }
      }
    },
    [rawHandleEditChange],
  );

  // Check if values have changed from initial
  const isDirty = useCallback(() => {
    if (!initialValues) return false;
    return JSON.stringify(state) !== JSON.stringify(initialValues);
  }, [state, initialValues]);

  // Subscribe to panel opened events
  useEffect(() => {
    if (!widgetId) return;

    const unsubscribe = subscribeToPanelOpened((openedWidgetId) => {
      if (openedWidgetId !== widgetId && isPanelOpen) {
        if (isDirty()) {
          setShowExternalCloseDialog(true);
        } else {
          setIsPanelOpen(false);
        }
      }
    });

    return unsubscribe;
  }, [widgetId, isPanelOpen, isDirty]);

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  const fontFamilyCss = fontData
    ? fontData.format === 'system'
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : 'inherit';
  const needsFontFace = fontData && fontData.format !== 'system' && fontData.format !== 'google';
  const fontFaceCss = needsFontFace
    ? `@font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }`
    : '';

  // Render the children with current state
  const widgetContent = (
    <>
      {fontFaceCss && <style>{fontFaceCss}</style>}
      {children({ ...state, fontFamilyCss }, resolveColor)}
    </>
  );

  // If not in edit mode, just render children
  if (!isEditMode) {
    return <>{widgetContent}</>;
  }

  const openPanel = () => {
    if (settingButtonRef.current) {
      const rect = settingButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const panelWidth = 620;
      const panelHeight = 450;
      const gap = 10;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = viewportWidth - rect.left;
      const spaceLeft = rect.right;

      let y: number;
      if (spaceBelow >= panelHeight + gap) {
        y = rect.bottom + gap;
      } else if (spaceAbove >= panelHeight + gap) {
        y = rect.top - panelHeight - gap;
      } else {
        if (spaceBelow >= spaceAbove) {
          y = Math.max(gap, viewportHeight - panelHeight - gap);
        } else {
          y = Math.max(gap, rect.top - panelHeight - gap);
        }
      }

      let x: number;
      if (spaceRight >= panelWidth) {
        x = rect.left;
      } else if (spaceLeft >= panelWidth) {
        x = rect.right - panelWidth;
      } else {
        x = Math.max(gap, Math.min(viewportWidth - panelWidth - gap, rect.left));
      }

      x = Math.max(gap, Math.min(x, viewportWidth - panelWidth - gap));
      y = Math.max(gap, Math.min(y, viewportHeight - panelHeight - gap));

      setScreenPosition({ x, y });
    }
    setInitialValues({ ...state });
    setIsPanelOpen(true);
    if (widgetId) {
      dispatchPanelOpened(widgetId);
    }
  };

  const handleReset = () => {
    if (initialValues) {
      Object.keys(initialValues).forEach((key) => {
        handleEditChange(key, initialValues[key]);
      });
    }
  };

  const handleSave = () => {
    if (saveWidget && canSave) {
      saveWidget();
    }
    setInitialValues({ ...state });
  };

  const handleExternalDiscard = () => {
    setShowExternalCloseDialog(false);
    setIsPanelOpen(false);
  };

  const handleExternalSave = () => {
    handleSave();
    setShowExternalCloseDialog(false);
    setIsPanelOpen(false);
  };

  return (
    <>
      <style>{`
        .floating-button-edit-icon {
          width: 32px;
          height: 32px;
          background: ${colors.white};
          border: 1px solid ${colors.gray_border};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: all 0.2s;
          padding: 0;
          opacity: 0;
          pointer-events: none;
        }

        .floating-button-edit-icon.visible {
          opacity: 1;
          pointer-events: auto;
        }

        .floating-button-edit-icon:hover {
          background: ${colors.blue_light};
          border-color: ${colors.blue};
        }
      `}</style>

      {/* Wrapper div to detect hover on both button and edit icon */}
      <div
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{ display: 'contents' }}
      >
        {widgetContent}

        <button
          ref={settingButtonRef}
          className={`floating-button-edit-icon ${isHovering || isPanelOpen ? 'visible' : ''}`}
          style={getEditIconPosition(state)}
          onMouseDown={(e) => {
            // Stop mousedown from propagating to document handlers (e.g., ConciergeWidget's click-outside)
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            openPanel();
          }}
          aria-label="Edit widget"
        >
          <Pencil size={16} color={colors.gray_dark} />
        </button>
      </div>

      {isPanelOpen && (
        <EditPanel
          widgetId={widgetId}
          displayName={displayName}
          fields={editFields}
          values={state}
          initialValues={initialValues || undefined}
          onChange={handleEditChange}
          onClose={() => setIsPanelOpen(false)}
          onReset={handleReset}
          screenPosition={screenPosition}
          onSave={handleSave}
          isSaving={isSaving}
          themeColors={themeColors}
          studioLockedKeys={studioLockedKeys}
        />
      )}

      {showExternalCloseDialog && (
        <ConfirmDialog
          title="Unsaved Changes"
          message="You have unsaved changes. Would you like to save them before closing?"
          confirmLabel="Save"
          cancelLabel="Discard"
          onConfirm={handleExternalSave}
          onCancel={handleExternalDiscard}
          isLoading={isSaving}
        />
      )}
    </>
  );
};
